import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Sparkles, ArrowRight } from 'lucide-react';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types';

export const ComingSoon: React.FC = () => {
  const { tenant } = useTenant();
  const { user, isLoading: authLoading } = useAuth();

  // Students and instructors who somehow land on the unpublished public face
  // are bounced to their role home — they're meant to use the app, not see
  // the marketing page. Admins stay on this page so they can preview what
  // visitors see. A small "Back to admin" link at the bottom lets them
  // navigate out without getting stuck.
  if (!authLoading && user) {
    if (user.role === UserRole.INSTRUCTOR) return <Navigate to="/instructor" replace />;
    if (user.role === UserRole.STUDENT) return <Navigate to="/browse" replace />;
  }

  const isAdmin = !authLoading && user?.role === UserRole.ADMIN;

  const name = tenant?.name || 'This site';
  const primary = tenant?.branding.primaryColor || '#BF1717';
  const secondary = tenant?.branding.secondaryColor || '#27265F';

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background: `linear-gradient(135deg, ${secondary}08, ${primary}0a)`,
      }}
    >
      <div className="text-center max-w-lg">
        {tenant?.logoUrl ? (
          <img
            src={tenant.logoUrl}
            alt={`${name} logo`}
            className="h-12 w-auto mx-auto mb-8 object-contain"
          />
        ) : (
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-8"
            style={{ backgroundColor: `${primary}15` }}
          >
            <Sparkles className="h-7 w-7" style={{ color: primary }} />
          </div>
        )}
        <h1 className="text-4xl font-bold font-serif text-gray-900 dark:text-white mb-3">
          Coming soon
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
          {name} is getting ready. Check back shortly.
        </p>
        <Link
          to={isAdmin ? '/admin' : '/login'}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          {isAdmin ? 'Back to admin' : 'Admin sign in'}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
};
